/**
 * SmartCalc — calculator + unit converter widget. Pure buttons, no typing.
 *
 * Modes (toggled via 3 chips at the top):
 *   1. Calc — standard +/-/×/÷ calculator with %, parentheses, sign-flip.
 *   2. Tip — bill amount × tip % × split count → per-person amount.
 *   3. Units — distance, weight, temperature with one-tap pair flip.
 *
 * Why a widget, not a full module: 70% of "calculator" intent is a 3-tap
 * arithmetic ("17.5 × 3", "what's 18% tip on 42") that doesn't need a
 * dedicated screen. Heavy users still tap "Open in calculator app" to
 * jump to the full SumApp (when it's wired into the store).
 *
 * Numerics: arithmetic is evaluated via the Function constructor on a
 * sanitized expression — only digits, + - * / . ( ) % spaces allowed.
 * Anything else is rejected before evaluation. No eval(), no math.js
 * dependency.
 */

import type { Widget, WidgetContext } from '../types';

type Mode = 'calc' | 'tip' | 'units';

const STYLE_ID = 'aweni-smartcalc-styles';

function injectStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    .scalc-card { background:var(--ln-bg-card,var(--card-bg,#fff)); border:1px solid var(--ln-border,var(--border,rgba(0,0,0,0.06))); border-radius:16px; padding:14px; }
    .scalc-tabs { display:flex; gap:6px; margin-bottom:10px; }
    .scalc-tab { flex:1; padding:8px 10px; min-height:36px; border-radius:10px; border:1px solid var(--ln-border,rgba(0,0,0,0.06)); background:transparent; font-size:12px; font-weight:600; cursor:pointer; color:inherit; }
    .scalc-tab[data-active="1"] { background:var(--ln-accent,#0A0A0A); color:var(--ln-accent-text,#fff); border-color:var(--ln-accent,#0A0A0A); }
    .scalc-display { background:var(--ln-bg-secondary,var(--bg-secondary,rgba(0,0,0,0.03))); border-radius:10px; padding:12px 14px; font-variant-numeric:tabular-nums; min-height:54px; display:flex; flex-direction:column; align-items:flex-end; justify-content:center; }
    .scalc-expr { font-size:12px; color:var(--ln-text-secondary,var(--text-secondary,#6b6657)); min-height:14px; word-break:break-all; }
    .scalc-out { font-size:24px; font-weight:800; color:var(--ln-text-primary,var(--text-primary,#1E293B)); word-break:break-all; }
    .scalc-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:6px; margin-top:8px; }
    .scalc-key { padding:0; min-height:48px; border-radius:10px; border:1px solid var(--ln-border,rgba(0,0,0,0.06)); background:var(--ln-bg-card,#fff); font-size:16px; font-weight:600; cursor:pointer; color:var(--ln-text-primary,var(--text-primary,#1E293B)); }
    .scalc-key:active { transform:scale(0.97); }
    .scalc-key[data-role="op"] { background:var(--ln-bg-secondary,rgba(0,0,0,0.04)); }
    .scalc-key[data-role="eq"] { background:var(--ln-accent,#0A0A0A); color:var(--ln-accent-text,#fff); border-color:var(--ln-accent,#0A0A0A); }
    .scalc-key[data-role="clr"] { color:#dc2626; }
    .scalc-stepper { display:flex; align-items:center; gap:8px; margin-top:8px; }
    .scalc-stepper button { width:36px; height:36px; border-radius:8px; border:1px solid var(--ln-border,rgba(0,0,0,0.06)); background:var(--ln-bg-card,#fff); font-size:16px; font-weight:700; cursor:pointer; color:inherit; }
    .scalc-row { display:flex; align-items:center; justify-content:space-between; padding:6px 0; }
    .scalc-row label { font-size:12px; color:var(--ln-text-secondary,var(--text-secondary,#6b6657)); text-transform:uppercase; letter-spacing:0.4px; }
    .scalc-row .val { font-size:16px; font-weight:700; font-variant-numeric:tabular-nums; }
    .scalc-pair { display:flex; gap:6px; flex-wrap:wrap; margin:6px 0; }
    .scalc-pair button { padding:6px 10px; border-radius:999px; border:1px solid var(--ln-border,rgba(0,0,0,0.10)); background:transparent; font-size:11px; font-weight:600; cursor:pointer; color:inherit; }
    .scalc-pair button[data-active="1"] { background:var(--ln-accent,#0A0A0A); color:var(--ln-accent-text,#fff); border-color:var(--ln-accent,#0A0A0A); }
  `;
  document.head.appendChild(s);
}

/** Tight expression evaluator: only digits, +, -, *, /, ., (, ), %, whitespace. */
function evalExpression(expr: string): number | null {
  if (!expr) return null;
  // Replace × ÷ unicode with * /
  const cleaned = expr.replace(/×/g, '*').replace(/÷/g, '/').replace(/\s+/g, '');
  if (!/^[-+*/.()%0-9]+$/.test(cleaned)) return null;
  // Convert "<n>%" suffixes into "(<n>/100)" so 50% becomes 0.5 in any context.
  const withPercent = cleaned.replace(/(\d+(?:\.\d+)?)%/g, '($1/100)');
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function('"use strict"; return (' + withPercent + ');');
    const r = fn();
    if (typeof r === 'number' && Number.isFinite(r)) return r;
    return null;
  } catch {
    return null;
  }
}

function formatNumber(n: number): string {
  if (Math.abs(n) >= 1e9) return n.toExponential(4);
  // Strip trailing zeros after decimal
  const s = n.toFixed(8);
  return s.replace(/\.?0+$/, '') || '0';
}

interface UnitPair { id: string; label: string; from: string; to: string; factor?: number; tx?: (v: number, dir: 1 | -1) => number; }

/** Built-in unit pairs. Tap to flip direction (handled in render). */
function unitPairs(): UnitPair[] {
  return [
    { id: 'km_mi', label: 'km ↔ mi', from: 'km', to: 'mi', factor: 0.621371 },
    { id: 'kg_lb', label: 'kg ↔ lb', from: 'kg', to: 'lb', factor: 2.20462 },
    { id: 'cm_in', label: 'cm ↔ in', from: 'cm', to: 'in', factor: 0.393701 },
    { id: 'l_gal', label: 'L ↔ gal (US)', from: 'L', to: 'gal', factor: 0.264172 },
    {
      id: 'c_f', label: '°C ↔ °F', from: '°C', to: '°F',
      tx: (v, dir) => dir === 1 ? v * 9 / 5 + 32 : (v - 32) * 5 / 9,
    },
  ];
}

export const SmartCalc: Widget = {
  id: 'smart-calc',
  label: {
    en: 'Calculator',
    fr: 'Calculatrice',
    ar: 'آلة حاسبة',
    es: 'Calculadora',
    de: 'Rechner',
  },
  icon: '🧮',
  defaultVisible: true,
  defaultOrder: 16,
  defaultSize: 'compact',

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    void size;
    injectStyles();
    const t = ctx.t;

    container.innerHTML = `
      <div class="scalc-card">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
          <span style="font-size:18px;">🧮</span>
          <span style="font-size:13px;font-weight:700;">${escapeHtml(t('widget_calc_title') || 'Calculator')}</span>
        </div>
        <div class="scalc-tabs" data-tabs>
          <button class="scalc-tab" data-mode="calc" data-active="1">${escapeHtml(t('widget_calc_tab_calc') || 'Calc')}</button>
          <button class="scalc-tab" data-mode="tip" data-active="0">${escapeHtml(t('widget_calc_tab_tip') || 'Tip')}</button>
          <button class="scalc-tab" data-mode="units" data-active="0">${escapeHtml(t('widget_calc_tab_units') || 'Units')}</button>
        </div>
        <div data-pane></div>
      </div>
    `;

    const pane = container.querySelector('[data-pane]') as HTMLElement;

    let mode: Mode = 'calc';
    const tabs = container.querySelectorAll<HTMLButtonElement>('[data-mode]');
    tabs.forEach((b) => {
      b.addEventListener('click', () => {
        mode = (b.dataset.mode as Mode) || 'calc';
        tabs.forEach((bb) => { bb.dataset.active = bb.dataset.mode === mode ? '1' : '0'; });
        renderPane();
      });
    });

    // -------- pane renderers ---------
    let calcExpr = '';

    const renderCalcPane = (): void => {
      const result = evalExpression(calcExpr);
      pane.innerHTML = `
        <div class="scalc-display">
          <div class="scalc-expr">${escapeHtml(calcExpr || '0')}</div>
          <div class="scalc-out">${result !== null ? escapeHtml(formatNumber(result)) : '—'}</div>
        </div>
        <div class="scalc-grid">
          <button class="scalc-key" data-role="clr" data-key="C">C</button>
          <button class="scalc-key" data-role="op" data-key="(">(</button>
          <button class="scalc-key" data-role="op" data-key=")">)</button>
          <button class="scalc-key" data-role="op" data-key="÷">÷</button>
          <button class="scalc-key" data-key="7">7</button>
          <button class="scalc-key" data-key="8">8</button>
          <button class="scalc-key" data-key="9">9</button>
          <button class="scalc-key" data-role="op" data-key="×">×</button>
          <button class="scalc-key" data-key="4">4</button>
          <button class="scalc-key" data-key="5">5</button>
          <button class="scalc-key" data-key="6">6</button>
          <button class="scalc-key" data-role="op" data-key="-">−</button>
          <button class="scalc-key" data-key="1">1</button>
          <button class="scalc-key" data-key="2">2</button>
          <button class="scalc-key" data-key="3">3</button>
          <button class="scalc-key" data-role="op" data-key="+">+</button>
          <button class="scalc-key" data-key="%">%</button>
          <button class="scalc-key" data-key="0">0</button>
          <button class="scalc-key" data-key=".">.</button>
          <button class="scalc-key" data-role="eq" data-key="=">=</button>
        </div>
      `;
      pane.querySelectorAll<HTMLButtonElement>('[data-key]').forEach((b) => {
        b.addEventListener('click', () => {
          const k = b.dataset.key || '';
          if (k === 'C') { calcExpr = ''; }
          else if (k === '=') {
            const r = evalExpression(calcExpr);
            if (r !== null) calcExpr = formatNumber(r);
          } else {
            calcExpr += k;
          }
          renderCalcPane();
        });
      });
    };

    const tip = { bill: 50, pct: 15, split: 1 };
    const renderTipPane = (): void => {
      const total = tip.bill * (1 + tip.pct / 100);
      const perPerson = total / tip.split;
      const tipAmount = tip.bill * (tip.pct / 100);
      pane.innerHTML = `
        <div class="scalc-display">
          <div class="scalc-expr">${escapeHtml(t('widget_calc_tip_total') || 'Total with tip')}</div>
          <div class="scalc-out">${escapeHtml(formatNumber(total))}</div>
        </div>
        <div style="margin-top:10px;">
          <div class="scalc-row">
            <label>${escapeHtml(t('widget_calc_tip_bill') || 'Bill')}</label>
            <div class="scalc-stepper">
              <button data-tip="bill-">−</button>
              <span class="val">${formatNumber(tip.bill)}</span>
              <button data-tip="bill+">+</button>
            </div>
          </div>
          <div class="scalc-row">
            <label>${escapeHtml(t('widget_calc_tip_pct') || 'Tip %')}</label>
            <div class="scalc-stepper">
              <button data-tip="pct-">−</button>
              <span class="val">${tip.pct}%</span>
              <button data-tip="pct+">+</button>
            </div>
          </div>
          <div class="scalc-row">
            <label>${escapeHtml(t('widget_calc_tip_split') || 'Split')}</label>
            <div class="scalc-stepper">
              <button data-tip="split-">−</button>
              <span class="val">${tip.split}</span>
              <button data-tip="split+">+</button>
            </div>
          </div>
          <div class="scalc-row" style="border-top:1px solid var(--ln-border,rgba(0,0,0,0.06));margin-top:6px;padding-top:8px;">
            <label>${escapeHtml(t('widget_calc_tip_per_person') || 'Per person')}</label>
            <span class="val">${escapeHtml(formatNumber(perPerson))}</span>
          </div>
          <div class="scalc-row">
            <label>${escapeHtml(t('widget_calc_tip_tip_amount') || 'Tip amount')}</label>
            <span class="val">${escapeHtml(formatNumber(tipAmount))}</span>
          </div>
        </div>
      `;
      pane.querySelectorAll<HTMLButtonElement>('[data-tip]').forEach((b) => {
        b.addEventListener('click', () => {
          const k = b.dataset.tip || '';
          if (k === 'bill+') tip.bill = Math.min(99999, tip.bill + (tip.bill < 100 ? 5 : 10));
          else if (k === 'bill-') tip.bill = Math.max(1, tip.bill - (tip.bill <= 100 ? 5 : 10));
          else if (k === 'pct+') tip.pct = Math.min(50, tip.pct + 1);
          else if (k === 'pct-') tip.pct = Math.max(0, tip.pct - 1);
          else if (k === 'split+') tip.split = Math.min(20, tip.split + 1);
          else if (k === 'split-') tip.split = Math.max(1, tip.split - 1);
          renderTipPane();
        });
      });
    };

    const pairs = unitPairs();
    let activePairId = pairs[0].id;
    let unitVal = 1;
    let unitDir: 1 | -1 = 1;

    const computeUnit = (): { from: string; to: string; result: number } => {
      const p = pairs.find((pp) => pp.id === activePairId) || pairs[0];
      const from = unitDir === 1 ? p.from : p.to;
      const to = unitDir === 1 ? p.to : p.from;
      let result = 0;
      if (p.tx) result = p.tx(unitVal, unitDir);
      else if (p.factor) result = unitDir === 1 ? unitVal * p.factor : unitVal / p.factor;
      return { from, to, result };
    };

    const renderUnitsPane = (): void => {
      const { from, to, result } = computeUnit();
      pane.innerHTML = `
        <div class="scalc-pair">
          ${pairs.map((p) => `<button data-pair="${p.id}" data-active="${p.id === activePairId ? '1' : '0'}">${escapeHtml(p.label)}</button>`).join('')}
        </div>
        <div class="scalc-display">
          <div class="scalc-expr">${unitVal} ${escapeHtml(from)} =</div>
          <div class="scalc-out">${escapeHtml(formatNumber(result))} ${escapeHtml(to)}</div>
        </div>
        <div class="scalc-row" style="margin-top:8px;">
          <label>${escapeHtml(t('widget_calc_units_value') || 'Value')}</label>
          <div class="scalc-stepper">
            <button data-unit="-">−</button>
            <span class="val">${unitVal}</span>
            <button data-unit="+">+</button>
            <button data-unit="flip" style="margin-inline-start:auto;">⇄</button>
          </div>
        </div>
      `;
      pane.querySelectorAll<HTMLButtonElement>('[data-pair]').forEach((b) => {
        b.addEventListener('click', () => {
          activePairId = b.dataset.pair || activePairId;
          renderUnitsPane();
        });
      });
      pane.querySelectorAll<HTMLButtonElement>('[data-unit]').forEach((b) => {
        b.addEventListener('click', () => {
          const k = b.dataset.unit || '';
          if (k === '+') unitVal = Math.min(99999, unitVal + (unitVal < 10 ? 1 : unitVal < 100 ? 5 : 10));
          else if (k === '-') unitVal = Math.max(0.1, +(unitVal - (unitVal <= 10 ? 1 : unitVal <= 100 ? 5 : 10)).toFixed(2));
          else if (k === 'flip') unitDir = unitDir === 1 ? -1 : 1;
          renderUnitsPane();
        });
      });
    };

    const renderPane = (): void => {
      if (mode === 'calc') renderCalcPane();
      else if (mode === 'tip') renderTipPane();
      else renderUnitsPane();
    };
    renderPane();
  },
};

function escapeHtml(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
